import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { RRITimeFieldInput } from '../../interfaces/time-field-input.interface';

@Component({
  selector: 'rr-date-time-field',
  templateUrl: './date-time-field.component.html',
  styleUrls: ['./date-time-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateTimeFieldComponent),
      multi: true,
    },
  ],
})
export class DateTimeFieldComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  public bsConfig: Partial<BsDatepickerConfig>;
  public data: Date = null;
  public onChangeFn: any = (_: any) => Date;
  public onTouchFn: any = () => {};
  public control: NgControl;
  private errorMessages = new Map<string, () => string>();

  @Input() settings: RRITimeFieldInput;
  @Input() dateInputSettings?: Partial<BsDatepickerConfig>;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() customErrorMessages: Map<string, () => string>;
  @Output() clearInput: EventEmitter<boolean> = new EventEmitter();

  constructor(private inj: Injector) {
    this.bsConfig = {
      containerClass: 'theme-default',
      isAnimated: true,
      dateInputFormat: 'DD/MM/YYYY',
    };
    this.errorMessages.set('required', () => `Required`);
  }

  ngOnInit(): void {
    this.control = this.inj.get(NgControl, null);

    if (this.customErrorMessages && this.customErrorMessages.size) {
      this.errorMessages = new Map([
        ...Array.from(this.errorMessages.entries()),
        ...Array.from(this.customErrorMessages.entries()),
      ]);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['dateInputSettings'] &&
      changes['dateInputSettings'].previousValue !==
        changes['dateInputSettings'].currentValue
    ) {
      this.bsConfig = {
        ...this.bsConfig,
        ...this.dateInputSettings,
      };
    }
  }

  public onClearInput(): void {
    if (!this.settings.disableClearEvent) {
      if (this.settings.defaultValue) {
        this.data = this.settings.defaultValue;
      } else {
        this.data = null;
      }
    }
    this.clearInput.emit(true);
    this.onChange();
  }

  public onDateValueChange(event: Date, from: string): void {
    if (from == 'time') {
      this.onChange();
    } else {
      if (event && event instanceof Date && !isNaN(event.getTime())) {
        if (this.data != event) {
          this.data = event;
        }
        this.onChange();
      } else {
        if (event instanceof Date) {
          this.data = null;
          this.onChange();
        }
      }
    }
  }

  public writeValue(value: Date): void {
    this.data = value;
  }

  public registerOnChange(fn: Date): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  public onChange() {
    this.onChangeFn(this.data);
  }

  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, value } = this.control;

    return this.invalid ? dirty || touched || !!value : false;
  }

  public get errors(): Array<string> {
    if (!this.control) {
      return [];
    }

    const { errors } = this.control;
    return Object.keys(errors).map((key) => {
      return this.errorMessages.has(key)
        ? this.errorMessages.get(key)()
        : 'error: ' + key;
    });
  }
  public validate(_control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
